import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CopyToClipboard } from "react-copy-to-clipboard/lib/Component";
import { toggleShareModalAction } from "../../store/actions";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import { logEvent, logInteractionEvent } from "../../utils/appInsight";
import { trackAdobeAppClick, trackAdobeClick, trackAdobeSocialClick } from "../../utils/adobeLogging";
import constants from "../../utils/constants";
import { useTranslation } from "react-i18next";

const baseUrl = `${window.location.protocol}//${window.location.hostname}`;
const url = `${baseUrl}`;

const twitterUrl = `https://twitter.com/share?url=${encodeURIComponent(
  url
)}&text=Baseline App`;
const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
  url
)}`;
const linkedUrl = `https://www.linkedin.com/shareArticle?url=${encodeURIComponent(url)}&text=${encodeURIComponent("Share article title")}&summary=${encodeURIComponent("Summary")}&mini=true`;

const ShareModal = () => {
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);
  const dispatch = useDispatch();
  const section = useSelector((state) => state.progress.section);
  const lang = useSelector((state) => state.ui.lang);
  const { t } = useTranslation();

  const toggleModal = () => {
    dispatch(toggleShareModalAction());
  };

  const share = (socialUrl, network) => {
    const left = Math.round(window.screen.width / 2 - 640 / 2);
    const top = Math.round(window.screen.height / 2 - 480 / 2);
    const window_config = `width=${640},height=${480},left=${left},top=${top}`;

    window.open(
      socialUrl,
      "Share this",
      `${window_config},toolbar=no,menubar=no,scrollbars=no`
    );

    logEvent({
      name: "Shared",
      properties: {
        section,
        type: network,
      },
    });
    logInteractionEvent("Shared", {
      section,
      type: network,
    });
    trackAdobeAppClick({action:network, language: lang})
    trackAdobeSocialClick({
      socialTool: network,
      linkTitle: network,
      linkModule: `${section}-share-modal`,
      targetURL: socialUrl,
    });

    toggleModal();
  };

  const onCopy = () => {
    setCopied(true);
    logEvent({
      name: "Shared",
      properties: {
        section,
        type: "url",
      },
    });
    logInteractionEvent("Shared", {
      section,
      type: "url",
    });
    trackAdobeAppClick({action:'copy-link', language: lang})
    trackAdobeSocialClick({
      socialTool: 'copy-link',
      linkTitle: 'copy-link',
      linkModule: `${section}-share-badge-modal`,
      targetURL: null,
    });
    // trackAdobeClick(null,{
    //   linkTitle: "copy-link",
    //   linkModule: `${section}-share-badge-modal`,
    //   targetURL: url,
    // })
  };

  useOutsideClick(ref, toggleModal);

  return (
    <div
      style={{
        position: "fixed",
        zIndex: 10,
        minHeight: "100vh",
        top: 0,
        left: 0,
        width: "100%",
        transition: "all ease 0.3s",
        overflow: "hidden",
      }}
      className="flex justify-center items-center"
      adoberegion={`${section}-share-modal`}
    >
      <div
        className="bg-black"
        style={{
          position: "absolute",
          zIndex: 15,
          opacity: 0.7,
          minHeight: "100vh",
          top: 0,
          left: 0,
          width: "100%",
        }}
      />
      <div
        ref={ref}
        className="rounded bg-white py-20 px-20"
        style={{ opacity: 1, zIndex: 20 }}
      >
        <div className="text-center">{t("shareTo")}</div>

        <div className="flex justify-between mt-25">
          <div
            className="cursor-pointer px-20"
            onClick={() => share(linkedUrl, constants.socialPlatforms.linkedin)}
          >
            <div className="flex flex-col justify-center items-center">
              <div>
                <img
                  src="images/linkedin-icon.png"
                  alt="linkedin"
                  style={{ width: "48px" }}
                />
              </div>
              <div>{t("linkedIn")}</div>
            </div>
          </div>
          <div
            className="cursor-pointer px-20"
            onClick={() =>
              share(facebookUrl, constants.socialPlatforms.facebook)
            }
          >
            <div className="flex flex-col justify-center items-center">
              <div>
                <img
                  src="images/facebook-icon.png"
                  alt="facebook"
                  style={{ width: "48px" }}
                />
              </div>
              <div>{t("facebook")}</div>
            </div>
          </div>
        </div>

        <div className="flex justify-between mt-25">
          <div
            className="cursor-pointer px-20"
            onClick={() => share(twitterUrl, constants.socialPlatforms.twitter)}
          >
            <div className="flex flex-col justify-center items-center">
              <div>
                <img
                  src="images/twitter-icon.png"
                  alt="twitter"
                  style={{ width: "48px" }}
                />
              </div>
              <div>{t("twitter")}</div>
            </div>
          </div>
          <div className="cursor-pointer px-20">
            <CopyToClipboard text={url} onCopy={onCopy}>
              <div className="flex flex-col justify-center items-center">
                <div>
                  <img
                    src="images/share-link-icon.png"
                    alt="share link"
                    style={{ width: "48px" }}
                  />
                </div>
                <div>{t("copyLink")}</div>
              </div>
            </CopyToClipboard>
          </div>
        </div>

        {copied && (
          <div className="flex justify-center text-sm mt-5 px-5 py-6 bg-w-2 text-white rounded">
            {t("linkCopied")} <br />
            {url}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareModal;
